import React, { useState, useEffect } from 'react';
import { fehlertext } from '../lib/fehlertext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { settingsAPI } from '../lib/api';
import { Card } from '../components/ui/card';
import { Mail, Lock, LogIn, KeyRound, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import ZweiFaktorSchritt from '../components/zweifaktor/ZweiFaktorSchritt';
import { AnmeldeRahmen } from '../components/anmeldung/AnmeldeRahmen';
import {
  Titel, Unterzeile, Beschriftung, Feld, Auge, Hauptknopf, Nebenknopf, Oder, Fehlerkasten, TextLink,
} from '../components/anmeldung/Bausteine';
import { VERWALTUNGS_ROLLEN } from '../lib/rollen';

const BEGRUESSUNG = 'Willkommen bei Das Relationship Schulbegleitung.';

const Login = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { login, kontoNeuLaden } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  // Zwischen-Token nur im React-State, nie im Speicher.
  const [zweiFaktor, setZweiFaktor] = useState(null);
  const [begruessung, setBegruessung] = useState(BEGRUESSUNG);

  useEffect(() => {
    settingsAPI.getBranding()
      .then((response) => setBegruessung(response.data?.welcome_text || BEGRUESSUNG))
      .catch(() => { /* Branding nicht eingerichtet: Standardtext */ });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(email, password);
      if (result.zwischenToken) {
        setPassword('');
        setZweiFaktor({ art: result.zweiFaktor, zwischenToken: result.zwischenToken });
        return;
      }
      if (result.fehler) {
        setError(result.fehler);
        return;
      }
      if (VERWALTUNGS_ROLLEN.includes(result.userData?.role)) kontoNeuLaden();
      toast.success('Erfolgreich angemeldet!');
      navigate('/');
    } catch (err) {
      const message = fehlertext(err, 'Anmeldung fehlgeschlagen');
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const zurueckZurAnmeldung = (meldung) => {
    setZweiFaktor(null);
    setPassword('');
    setError(meldung || '');
  };

  if (zweiFaktor) {
    return (
      <AnmeldeRahmen breit={zweiFaktor.art === 'einrichtung_erforderlich'}>
        <Card className="border-[#e2e8f0] shadow-sm">
          <ZweiFaktorSchritt art={zweiFaktor.art} zwischenToken={zweiFaktor.zwischenToken} onZurueck={zurueckZurAnmeldung} email={email} />
        </Card>
      </AnmeldeRahmen>
    );
  }

  return (
    <AnmeldeRahmen>
      <Titel testid="login-titel">Anmelden</Titel>
      <Unterzeile testid="login-welcome-text">{begruessung}</Unterzeile>
      {params.get('grund') === '30tage' && (
        <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800" data-testid="login-hinweis-30tage">
          Zu Ihrer Sicherheit melden Sie sich alle 30 Tage neu an.
        </div>
      )}
      <form onSubmit={handleSubmit} className="mt-6 space-y-5" data-testid="login-form">
        {error && <Fehlerkasten testid="login-fehler">{error}</Fehlerkasten>}
        <div>
          <Beschriftung htmlFor="email">E-Mail-Adresse</Beschriftung>
          <Feld id="email" type="email" symbol={Mail} placeholder="name@beispiel.de" autoComplete="username" value={email}
            onChange={(e) => setEmail(e.target.value)} required data-testid="login-email-input" />
        </div>
        <div>
          <Beschriftung htmlFor="password">Passwort</Beschriftung>
          <Feld id="password" type={showPassword ? 'text' : 'password'} symbol={Lock} placeholder="••••••••" autoComplete="current-password"
            value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password-input"
            rechts={<Auge sichtbar={showPassword} onClick={() => setShowPassword(!showPassword)} testid="toggle-password-visibility" />} />
          <div className="mt-1.5 text-right">
            <TextLink to="/forgot-password" testid="forgot-password-link">Passwort vergessen?</TextLink>
          </div>
        </div>
        <Hauptknopf type="submit" disabled={loading} data-testid="login-submit-button">
          {loading ? <><Loader2 className="h-4 w-4 animate-spin" />Wird angemeldet …</> : <><LogIn className="h-4 w-4" />Anmelden</>}
        </Hauptknopf>
        <Oder />
        <Nebenknopf to="/zugangscode" testid="have-access-code-login-link">
          <KeyRound className="h-4 w-4" />Ich habe einen Zugangscode
        </Nebenknopf>
      </form>
    </AnmeldeRahmen>
  );
};

export default Login;
